// src/modules/app-views/products/pages/Document/tabs/SuppliersTab/modals/SupplierModal/index.jsx
// *******************************************************************************
// SUPPLIER MODAL
// *******************************************************************************
// Two rendering modes:
// - ADD (new supplier): GenericModal with 2 steps (guided flow)
// - EDIT (existing supplier): GenericModal with 1 step containing Tabs (direct access)
// Both modes reuse the same Step1Content and Step2Content components.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Tabs } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import translateX from 'utils/translateX';
import { addProductSupplier, unsetAllPrimarySuppliers } from 'modules/app-views/products/service';
import step1fn from './Step1';
import step2fn from './Step2';
import { Step1Content } from './Step1';
import { Step2Content } from './Step2';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SupplierModal = ({ show, setShow, afterClose, onSuccess, editingSupplier, productId, productSalePrice = 0, productUnitUid = null }) => {
  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const isEdit = !!editingSupplier;

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------
  const [appliedCosts, setAppliedCosts] = useState([]);

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------
  // Initialize appliedCosts from editing supplier data
  useEffect(() => {
    if (show && editingSupplier?.appliedCosts) {
      setAppliedCosts(editingSupplier.appliedCosts);
    } else if (show) {
      setAppliedCosts([]);
    }
  }, [show, editingSupplier]);

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = useCallback(
    async ({ form, setLoading }) => {
      const values = form.getFieldsValue(true);
      setLoading(true);
      try {
        const supplierData = {
          ...(editingSupplier?._id ? { _id: editingSupplier._id } : {}),
          supplierId: values.supplierId,
          supplierName: values._supplierName || editingSupplier?.supplierName || '',
          description: values.description || '',
          sku: values.sku || '',
          unitUid: values.unitUid || null,
          cfop: values.cfop || '',
          taxSituation: values.taxSituation || '',
          warrantyDays: values.warrantyDays || 0,
          leadTimeDays: values.leadTimeDays || 0,
          notes: values.notes || '',
          listPrice: values.listPrice || 0,
          chainDiscount: values.chainDiscount || null,
          purchasePrice: values.purchasePrice || 0,
          priceAfterChainDiscount: values.priceAfterChainDiscount || 0,
          lastNfDiscountPercentage: values.lastNfDiscountPercentage || 0,
          priceAfterAllDiscounts: values.priceAfterAllDiscounts || 0,
          conversionFactor: values.conversionFactor || 1,
          convertedUnitCost: values.convertedUnitCost || 0,
          markup: values.markup || 50,
          appliedCosts,
          isPrimary: values.isPrimary || false,
          isActive: true,
        };

        // If marking as primary, unset all other suppliers first
        if (supplierData.isPrimary) {
          await unsetAllPrimarySuppliers(productId, supplierData._id);
        }

        await addProductSupplier({ productId, supplierData });
        onSuccess?.();
      } catch (err) {
        setLoading(false);
        return false;
      }
    },
    [editingSupplier, productId, appliedCosts, onSuccess],
  );

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ADD mode: 2 steps (guided flow)
  const addSteps = useMemo(
    () => [
      step1fn({ editingSupplier: null }),
      step2fn({ appliedCosts, setAppliedCosts, productSalePrice, productUnitUid }),
    ],
    [appliedCosts, productSalePrice, productUnitUid],
  );

  // EDIT mode: 1 step with Tabs inside (direct access to any section)
  const editSteps = useMemo(
    () => [
      {
        stepTitle: 'edit_supplier',
        validationFields: [],
        content: (
          <Tabs
            defaultActiveKey="item"
            items={[
              {
                key: 'item',
                label: translateX('item'),
                children: <Step1Content editingSupplier={editingSupplier} />,
              },
              {
                key: 'cost',
                label: translateX('cost'),
                children: (
                  <Step2Content
                    appliedCosts={appliedCosts}
                    setAppliedCosts={setAppliedCosts}
                    productSalePrice={productSalePrice}
                    productUnitUid={productUnitUid}
                  />
                ),
              },
            ]}
            destroyOnHidden={false}
          />
        ),
      },
    ],
    [editingSupplier, appliedCosts, setAppliedCosts],
  );

  const initialValues = useMemo(() => {
    if (!editingSupplier) return { _supplierName: '', markup: 50 };

    return {
      _supplierName: editingSupplier.supplierName || '',
      supplierId: editingSupplier.supplierId,
      description: editingSupplier.description || '',
      sku: editingSupplier.sku || '',
      unitUid: editingSupplier.unitUid || null,
      cfop: editingSupplier.cfop || '',
      taxSituation: editingSupplier.taxSituation || '',
      warrantyDays: editingSupplier.warrantyDays || 0,
      leadTimeDays: editingSupplier.leadTimeDays || 0,
      notes: editingSupplier.notes || '',
      listPrice: editingSupplier.listPrice || 0,
      chainDiscount: editingSupplier.chainDiscount || null,
      purchasePrice: editingSupplier.purchasePrice || 0,
      priceAfterChainDiscount: editingSupplier.priceAfterChainDiscount || 0,
      lastNfDiscountPercentage: editingSupplier.lastNfDiscountPercentage || 0,
      priceAfterAllDiscounts: editingSupplier.priceAfterAllDiscounts || 0,
      conversionFactor: editingSupplier.conversionFactor || 1,
      convertedUnitCost: editingSupplier.convertedUnitCost || 0,
      markup: editingSupplier.markup || 50,
      isPrimary: editingSupplier.isPrimary || false,
    };
  }, [editingSupplier]);

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<ShopOutlined />}
      title={isEdit ? translateX('edit_supplier') : translateX('add_supplier')}
      steps={isEdit ? editSteps : addSteps}
      onFinish={handleFinish}
      initialValues={initialValues}
      width={720}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SupplierModal;
